import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Eye, ArrowRight, CheckCircle2, Send, Loader2, Dice5, PartyPopper, Users, Plus, LogOut, Trophy, RotateCw, RefreshCw, UserCircle, Zap, Frown, Crown, WifiOff, XCircle, ChevronDown, PlayCircle } from 'lucide-react';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

const IS_PRODUCTION = window.location.hostname !== "localhost" && !window.location.hostname.startsWith("192.168");
const WS_PROTOCOL = IS_PRODUCTION ? "wss://" : "ws://";
const PROD_URL = "api.toptenapp.es";
const BASE_URL = IS_PRODUCTION ? PROD_URL : `${window.location.hostname}:8000`;

const getClientId = () => {
  let id = localStorage.getItem('topten_client_id');
  if (!id) { 
    id = Math.random().toString(36).substr(2, 9); 
    localStorage.setItem('topten_client_id', id); 
  }
  return id;
};

const CLIENT_ID = getClientId();
const EXTRA_EMOJIS = ['💩', '🔥', '🍻', '🤡', '👽', '💃', '🤬', '🤫', '🤮', '💯', '🚩', '🍆', '🦄', '🎉', '😡', '❤️'];

function App() {
  const { width, height } = useWindowSize();
  const [screen, setScreen] = useState("HOME");
  const [myName, setMyName] = useState(localStorage.getItem('topten_name') || "");
  const [roomCode, setRoomCode] = useState("");
  const [selectedMode, setSelectedMode] = useState("clasico");
  const [savedSession, setSavedSession] = useState(null);
  
  const [gameState, setGameState] = useState(null);
  const [localAnswers, setLocalAnswers] = useState({});
  const [customTheme, setCustomTheme] = useState("");
  const [showRerollScreen, setShowRerollScreen] = useState(false);
  const [rerollTheme, setRerollTheme] = useState("");
  
  const [recentEmojis, setRecentEmojis] = useState(['😂', '💀', '👀', '🤯']);
  const [showEmojiMenu, setShowEmojiMenu] = useState(false);
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  
  const [legalModal, setLegalModal] = useState(null);
  const [showCookies, setShowCookies] = useState(!localStorage.getItem('topten_cookies_accepted'));
  
  const [showRewardAd, setShowRewardAd] = useState(false);
  const [adCountdown, setAdCountdown] = useState(0);

  const socket = useRef(null);
  const pingInterval = useRef(null);

  useEffect(() => { 
    if (myName) localStorage.setItem('topten_name', myName); 
  }, [myName]);

  useEffect(() => {
    const savedRoom = localStorage.getItem('topten_current_room');
    const savedMode = localStorage.getItem('topten_current_mode');
    
    if (savedRoom) {
      setSavedSession({ room: savedRoom, mode: savedMode || "clasico" });
      if (myName) {
        connectToGame(savedRoom, savedMode || "clasico");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- LÓGICA DEL TEMPORIZADOR DEL ANUNCIO ---
  useEffect(() => {
    let timer;
    if (showRewardAd && adCountdown > 0) {
      timer = setTimeout(() => setAdCountdown(c => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [showRewardAd, adCountdown]);

  useEffect(() => {
    if (gameState?.question) { 
      setLocalAnswers({}); 
      setShowRerollScreen(false); 
    }
  }, [gameState?.question]);

  useEffect(() => { 
    setCustomTheme(""); 
  }, [gameState?.captainId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!socket.current || socket.current.readyState === WebSocket.CLOSED || socket.current.readyState === WebSocket.CLOSING) {
          const savedRoom = localStorage.getItem('topten_current_room');
          const savedMode = localStorage.getItem('topten_current_mode');
          if (savedRoom && myName) connectToGame(savedRoom, savedMode || "clasico");
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [myName]);

  const acceptCookies = () => {
    localStorage.setItem('topten_cookies_accepted', 'true');
    setShowCookies(false);
  };

  const connectToGame = (code, mode = "clasico") => {
    if (!myName.trim()) { 
      alert("¡Ponte un nombre primero!"); 
      return; 
    }
    
    const cleanCode = code.toUpperCase();
    const wsUrl = `${WS_PROTOCOL}${BASE_URL}/ws/${cleanCode}/${CLIENT_ID}?mode=${mode}`;
    
    if (socket.current) socket.current.close();
    if (pingInterval.current) clearInterval(pingInterval.current);
    
    setScreen("LOADING");
    socket.current = new WebSocket(wsUrl);

    socket.current.onopen = () => {
      socket.current.send(JSON.stringify({ type: "SET_NAME", name: myName }));
      localStorage.setItem('topten_current_room', cleanCode);
      localStorage.setItem('topten_current_mode', mode);
      setRoomCode(cleanCode);
      setScreen("GAME");
      
      pingInterval.current = setInterval(() => {
        if (socket.current.readyState === WebSocket.OPEN) {
          socket.current.send(JSON.stringify({ type: "PING" }));
        }
      }, 5000);
    };

    socket.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "UPDATE") {
        setGameState(data.state);
      } else if (data.type === "REACTION") {
        const count = 8;
        const newEmojis = Array.from({ length: count }).map(() => ({
          id: Date.now() + Math.random(),
          emoji: data.emoji,
          leftOffset: -40 + Math.random() * 80,
          delay: Math.random() * 0.4,
          duration: 2 + Math.random() * 1.5
        }));
        
        setFloatingEmojis(prev => [...prev, ...newEmojis]);
        
        setTimeout(() => {
          const idsToRemove = new Set(newEmojis.map(e => e.id));
          setFloatingEmojis(prev => prev.filter(e => !idsToRemove.has(e.id)));
        }, 4000);
      }
    };

    socket.current.onerror = () => {
      if (screen === "LOADING") { 
        alert("Error conectando."); 
        setScreen("HOME"); 
      }
    };
    
    socket.current.onclose = () => { 
      if (pingInterval.current) clearInterval(pingInterval.current); 
    };
  };

  const leaveGame = () => {
    localStorage.removeItem('topten_current_room'); 
    setSavedSession(null);
    if (socket.current) socket.current.close();
    setGameState(null); 
    setScreen("HOME"); 
    setRoomCode(""); 
    setLocalAnswers({});
  };

  const send = (type, payload = {}) => {
    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify({ type, ...payload }));
    }
  };

  const submitCardAnswer = (intensity, text) => {
    if (text && text.trim() !== "") {
      send("SUBMIT_ANSWER", { intensity, text });
    }
  };
  
  const sendReaction = (emoji) => {
    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify({ type: "REACTION", emoji }));
    }
  };

  const handleSelectExtraEmoji = (emoji) => {
    sendReaction(emoji);
    setRecentEmojis(prev => {
      const filtered = prev.filter(em => em !== emoji);
      return [emoji, ...filtered].slice(0, 4);
    });
    setShowEmojiMenu(false);
  };

  const onDragEnd = (result) => {
    if (!result.destination || !gameState) return;
    const items = Array.from(gameState.orderedResponses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setGameState(prev => ({ ...prev, orderedResponses: items }));
    send("UPDATE_ORDER", { order: items });
  };

  const btnClass = "transform active:scale-95 transition-all duration-150 ease-in-out disabled:opacity-50";

  const renderAppLeaveButton = () => {
    const handleExit = () => {
      if (screen === "GAME") {
        if (window.confirm("¿Salir de la sala?")) leaveGame();
      } else if (screen === "CREATE" || screen === "JOIN") {
        setScreen("HOME");
      } else if (screen === "HOME" && myName) {
        if (window.confirm("¿Borrar tu usuario y desconectar?")) {
          localStorage.clear(); 
          setMyName(""); 
          setSavedSession(null);
        }
      }
    };
    if (screen === "HOME" && !myName) return null;
    return (
      <button onClick={handleExit} className="absolute top-6 left-6 text-purple-400 hover:text-white transition-colors z-50 p-2">
        <LogOut size={24} />
      </button>
    );
  };

  const renderReactionBar = () => (
    <>
      {showEmojiMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowEmojiMenu(false)}></div>
          <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 bg-purple-900 border-2 border-purple-500 p-4 rounded-2xl shadow-2xl grid grid-cols-4 gap-4 z-50 w-[90vw] max-w-[320px] justify-items-center animate-in zoom-in">
            {EXTRA_EMOJIS.map(em => (
              <button key={em} onClick={() => handleSelectExtraEmoji(em)} className="text-3xl hover:scale-125 transition-transform active:scale-90">
                {em}
              </button>
            ))}
          </div>
        </>
      )}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 flex items-center z-40">
        <div className="bg-purple-950/90 backdrop-blur px-6 py-3 rounded-full flex gap-4 shadow-2xl border border-purple-500/30 items-center">
          {recentEmojis.map((emoji, idx) => (
            <button key={idx} onClick={() => sendReaction(emoji)} className="text-3xl active:scale-150 transition-transform hover:-translate-y-2">
              {emoji}
            </button>
          ))}
          <div className="w-px h-8 bg-purple-700 mx-2"></div>
          <button onClick={() => setShowEmojiMenu(!showEmojiMenu)} className="w-10 h-10 rounded-full bg-purple-800 flex items-center justify-center text-purple-300 hover:text-white active:scale-90 transition-transform">
            <Plus size={24}/>
          </button>
        </div>
      </div>
    </>
  );

  const renderFloatingEmojis = () => (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {floatingEmojis.map(e => (
        <div key={e.id} className="emoji-float drop-shadow-2xl absolute text-6xl flex justify-center w-16 -ml-8" style={{ left: `calc(50% + ${e.leftOffset}vw)`, animationDuration: `${e.duration}s`, animationDelay: `${e.delay}s` }}>
          {e.emoji}
        </div>
      ))}
    </div>
  );

  const renderRoomBadge = () => {
    if (!gameState?.roomCode) return null;
    return (
      <div className="absolute top-6 right-6 flex flex-col items-end z-40">
        <span className="text-[10px] font-bold text-purple-400 uppercase">Sala</span>
        <span className="text-xl font-black bg-purple-950/80 px-3 py-1 rounded-lg border border-purple-500/50 shadow-lg tracking-widest">
          {gameState.roomCode}
        </span>
      </div>
    );
  };

  const renderPlayerList = () => (
    <div className="w-full max-w-sm bg-purple-800/50 p-6 rounded-2xl border border-purple-700 mb-6 relative z-20">
      <p className="text-center text-purple-200 font-bold mb-4 uppercase text-sm">Jugadores en sala ({Object.keys(gameState.players).length})</p>
      <div className="flex flex-wrap gap-2 justify-center">
        {Object.values(gameState.players).map((p, i) => (
          <span key={i} className={`border px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2 animate-in zoom-in ${p.isOnline ? 'bg-purple-900 border-purple-600 opacity-100' : 'bg-red-900/50 border-red-800 opacity-50'}`}>
            {p.isCaptain && <Crown size={14} className="text-yellow-400"/>} 
            {p.name} 
            {!p.isOnline && <WifiOff size={12} />}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes floatUp { 
          0% { transform: translate3d(0, 10vh, 0) scale(0.5); opacity: 0; } 
          10% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); } 
          100% { transform: translate3d(0, -120vh, 0) scale(1.5); opacity: 0; } 
        }
        .emoji-float { 
          position: fixed; 
          bottom: 0; 
          animation-name: floatUp; 
          animation-timing-function: linear; 
          animation-fill-mode: forwards; 
          will-change: transform, opacity; 
        }
        ::-webkit-scrollbar { width: 8px; } 
        ::-webkit-scrollbar-track { background: #4c1d95; } 
        ::-webkit-scrollbar-thumb { background: #7c3aed; border-radius: 4px; }
      `}</style>

      {/* --- MODAL DE ANUNCIO RECOMPENSADO --- */}
      {showRewardAd && (
        <div className="fixed inset-0 bg-black/95 z-[9999] flex flex-col items-center justify-center p-4 animate-in fade-in backdrop-blur-sm">
          <h2 className="text-3xl font-black text-yellow-400 mb-2 text-center animate-pulse">+3 Cambios Gratis</h2>
          <p className="text-gray-300 font-bold mb-8 text-center max-w-sm">Apoya el juego viendo este anuncio durante unos segundos.</p>
          
          <div className="bg-white/10 p-2 rounded-2xl shadow-2xl mb-8 border border-white/20">
            <iframe src="/ad-300x250.html" width="300" height="250" frameBorder="0" scrolling="no" allowTransparency="true" style={{ border: 'none', borderRadius: '8px' }} sandbox="allow-scripts allow-popups allow-same-origin"></iframe>            
          </div>
          
          
          <div className="h-16 flex items-center justify-center">
            {adCountdown > 0 ? (
              <button disabled className="bg-gray-800 border-2 border-gray-600 text-gray-400 px-8 py-4 rounded-2xl font-black text-xl flex items-center gap-2">
                <Loader2 className="animate-spin" size={24}/> Espera {adCountdown}s...
              </button>
            ) : (
              <button onClick={() => { send("REWARD_REROLLS"); setShowRewardAd(false); }} className="bg-green-500 border-b-4 border-green-700 hover:bg-green-400 text-white px-8 py-4 rounded-2xl font-black text-xl shadow-[0_0_20px_rgba(34,197,94,0.4)] animate-bounce flex items-center gap-2">
                <CheckCircle2 size={24} /> ¡CERRAR Y RECIBIR!
              </button>
            )}
          </div>
        </div>
      )}

      {screen !== "GAME" && screen !== "LOADING" && (
        <div className="min-h-screen bg-purple-900 text-white relative overflow-y-auto">
          <div className="flex flex-col items-center justify-center min-h-screen p-6 relative">
            {renderAppLeaveButton()}
            
            <div className="mb-10 text-center z-10">
              <h1 className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 transform -rotate-3 mb-2">TOP TEN</h1>
            </div>
            
            <div className="w-full max-w-xs mb-8 z-10">
              <label className="block text-xs font-bold text-purple-300 uppercase mb-2 ml-1">Tu Nombre</label>
              <input className="w-full p-4 rounded-xl bg-purple-800/80 border-2 border-purple-600 text-white font-bold text-center text-xl focus:border-yellow-400 focus:outline-none" placeholder="Ej: Alex" value={myName} onChange={(e) => setMyName(e.target.value)} />
            </div>
            
            {screen === "HOME" && (
              <div className="flex flex-col gap-4 w-full max-w-xs z-10">
                <button onClick={() => setScreen("CREATE")} className={`bg-yellow-400 text-purple-900 p-5 rounded-2xl font-black text-lg flex items-center justify-between shadow-lg border-b-4 border-yellow-600 ${btnClass}`}>
                  <span>CREAR SALA</span><Plus strokeWidth={3}/>
                </button>
                <button onClick={() => setScreen("JOIN")} className={`bg-purple-600 text-white p-5 rounded-2xl font-black text-lg flex items-center justify-between shadow-lg border-b-4 border-purple-800 ${btnClass}`}>
                  <span>UNIRSE A SALA</span><Users strokeWidth={3}/>
                </button>
              </div>
            )}
            
            {screen === "CREATE" && (
              <div className="w-full max-w-xs z-10 animate-in fade-in">
                <p className="text-center text-sm font-bold text-purple-300 mb-4 mt-4">ELIGE EL MODO</p>
                <div className="grid grid-cols-3 gap-2 mb-6">
                  <button onClick={() => setSelectedMode("clasico")} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 ${selectedMode === "clasico" ? "bg-blue-600 border-white scale-105" : "bg-purple-800 border-purple-600 opacity-60"}`}>
                    <Dice5 size={24}/><span className="font-bold text-[10px]">Clásico</span>
                  </button>
                  <button onClick={() => setSelectedMode("fiesta")} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 ${selectedMode === "fiesta" ? "bg-pink-600 border-white scale-105" : "bg-purple-800 border-purple-600 opacity-60"}`}>
                    <PartyPopper size={24}/><span className="font-bold text-[10px]">Fiesta</span>
                  </button>
                  <button onClick={() => setSelectedMode("personalizado")} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 ${selectedMode === "personalizado" ? "bg-orange-500 border-white scale-105" : "bg-purple-800 border-purple-600 opacity-60"}`}>
                    <UserCircle size={24}/><span className="font-bold text-[10px]">Propio</span>
                  </button>
                </div>
                <button onClick={() => connectToGame(Math.random().toString(36).substring(2, 6).toUpperCase(), selectedMode)} disabled={!myName} className={`w-full bg-green-500 text-white p-4 rounded-xl font-black text-xl shadow-lg border-b-4 border-green-700 ${btnClass}`}>
                  ¡CREAR!
                </button>
              </div>
            )}
            
            {screen === "JOIN" && (
              <div className="w-full max-w-xs z-10 animate-in fade-in">
                <p className="text-center text-sm font-bold text-purple-300 mb-4 mt-4">CÓDIGO DE SALA</p>
                <input className="w-full p-4 rounded-xl bg-white text-purple-900 font-black text-center text-3xl mb-6 tracking-widest uppercase focus:outline-none ring-4 ring-purple-600" placeholder="A B C D" maxLength={4} value={roomCode} onChange={(e) => setRoomCode(e.target.value.toUpperCase())}/>
                <button onClick={() => { if(roomCode.length>=4) connectToGame(roomCode); }} disabled={!myName || roomCode.length < 4} className={`w-full bg-blue-500 text-white p-4 rounded-xl font-black text-xl shadow-lg border-b-4 border-blue-700 ${btnClass}`}>
                  ENTRAR
                </button>
              </div>
            )}

            {screen === "HOME" && (
              <div className="absolute bottom-8 animate-bounce opacity-50 flex flex-col items-center">
                <span className="text-xs uppercase font-bold tracking-widest mb-1 text-purple-300">Descubre Más</span>
                <ChevronDown size={24} className="text-purple-300"/>
              </div>
            )}
          </div>

          {screen === "HOME" && (
            <div className="max-w-3xl mx-auto p-6 pb-24 space-y-12 text-purple-200">
              <section className="bg-purple-800/40 p-6 rounded-2xl border border-purple-700/50">
                <h2 className="text-2xl font-black text-white mb-4">¿Qué es Top Ten? El Mejor Party Game Online</h2>
                <p className="mb-4 leading-relaxed">Top Ten es un juego de mesa cooperativo multijugador adaptado para jugar online gratis con tus amigos. El objetivo es muy sencillo: todos los jugadores reciben una "intensidad" secreta del 1 al 10 y deben dar una respuesta acorde a un tema común. ¡El Capitán debe ordenarlas de menor a mayor!</p>
                <p className="leading-relaxed">Ya sea que estéis en la misma habitación o conectados por videollamada, Top Ten Online garantiza risas, debates absurdos y momentos inolvidables. Sin descargas, directamente desde el navegador de tu móvil u ordenador.</p>
              </section>

              <section>
                <h2 className="text-2xl font-black text-white mb-4">Reglas del Juego</h2>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <span className="bg-yellow-500 text-purple-900 w-6 h-6 rounded-full flex items-center justify-center font-black shrink-0">1</span> 
                    <div><strong>El Tema:</strong> El Capitán de la ronda lee una temática (Ej: "Cosas que llevarías a una isla desierta, de inútil a imprescindible").</div>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-yellow-500 text-purple-900 w-6 h-6 rounded-full flex items-center justify-center font-black shrink-0">2</span> 
                    <div><strong>Las Cartas:</strong> Cada jugador recibe una carta secreta con un número del 1 al 10. (1 es lo más bajo/peor, 10 es lo más alto/mejor).</div>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-yellow-500 text-purple-900 w-6 h-6 rounded-full flex items-center justify-center font-black shrink-0">3</span> 
                    <div><strong>Las Respuestas:</strong> Cada jugador inventa una respuesta que coincida con su número.</div>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-yellow-500 text-purple-900 w-6 h-6 rounded-full flex items-center justify-center font-black shrink-0">4</span> 
                    <div><strong>La Revelación:</strong> El Capitán debe ordenar las respuestas de menor a mayor. ¿Lograréis un Orden Perfecto?</div>
                  </li>
                </ul>
              </section>

              <section className="grid md:grid-cols-3 gap-4">
                <div className="bg-blue-900/30 p-4 rounded-xl border border-blue-500/30">
                  <Dice5 size={32} className="text-blue-400 mb-2"/>
                  <h3 className="font-bold text-white mb-1">Modo Clásico</h3>
                  <p className="text-sm">Preguntas divertidas y para todos los públicos. Ideal para jugar en familia o romper el hielo.</p>
                </div>
                <div className="bg-pink-900/30 p-4 rounded-xl border border-pink-500/30">
                  <PartyPopper size={32} className="text-pink-400 mb-2"/>
                  <h3 className="font-bold text-white mb-1">Modo Fiesta</h3>
                  <p className="text-sm">Temas un poco más picantes, absurdos o de humor negro. Para cuando hay confianza.</p>
                </div>
                <div className="bg-orange-900/30 p-4 rounded-xl border border-orange-500/30">
                  <UserCircle size={32} className="text-orange-400 mb-2"/>
                  <h3 className="font-bold text-white mb-1">Modo Propio</h3>
                  <p className="text-sm">Libertad total. El Capitán inventa el tema sobre la marcha. Las risas están aseguradas.</p>
                </div>
              </section>

              {/* Footer Legal */}
              <footer className="border-t border-purple-700/50 pt-8 mt-12 flex flex-col items-center gap-4 text-sm text-purple-400 pb-8">
                <div className="flex gap-6">
                  <button onClick={() => setLegalModal('privacy')} className="hover:text-white transition-colors">Política de Privacidad</button>
                  <button onClick={() => setLegalModal('terms')} className="hover:text-white transition-colors">Términos de Uso</button>
                </div>
                <p>© {new Date().getFullYear()} Top Ten Online. Todos los derechos reservados.</p>
              </footer>
            </div>
          )}

          {/* Modales Legales */}
          {legalModal && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
              <div className="bg-purple-900 border-2 border-purple-500 rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col shadow-2xl">
                <div className="p-4 border-b border-purple-700/50 flex justify-between items-center bg-purple-950/50 rounded-t-2xl">
                  <h3 className="font-black text-xl text-white">{legalModal === 'privacy' ? 'Política de Privacidad' : 'Términos de Uso'}</h3>
                  <button onClick={() => setLegalModal(null)} className="text-purple-400 hover:text-white transition-colors"><XCircle size={28}/></button>
                </div>
                <div className="p-6 overflow-y-auto text-sm text-purple-200 space-y-4 leading-relaxed">
                  {legalModal === 'privacy' ? (
                    <>
                      <p>En Top Ten Online respetamos profundamente tu privacidad. Nuestra aplicación está diseñada para no recopilar ni almacenar información personal identificable.</p>
                      <p><strong>1. Datos de usuario:</strong> El nombre que introduces al entrar al juego se utiliza de forma temporal para identificarte dentro de la sala de juego. Este nombre se guarda únicamente en el almacenamiento local de tu dispositivo (`localStorage`) para facilitar reconexiones rápidas si cierras la pestaña por accidente.</p>
                      <p><strong>2. Almacenamiento Local:</strong> Utilizamos cookies técnicas y almacenamiento local estrictamente necesarios para el funcionamiento del juego (como mantenerte en tu sala). No utilizamos cookies de rastreo de terceros para obtener datos personales.</p>
                      <p><strong>3. Anuncios:</strong> Esta página utiliza redes publicitarias de terceros para mostrar anuncios. Los proveedores pueden utilizar cookies para personalizar los anuncios basados en tus visitas anteriores en Internet.</p>
                    </>
                  ) : (
                    <>
                      <p>Al utilizar Top Ten Online, aceptas los siguientes términos y condiciones de uso.</p>
                      <p><strong>1. Uso aceptable:</strong> Este juego es una plataforma de entretenimiento. No nos hacemos responsables del contenido generado por los usuarios en el Modo Propio ni de las respuestas introducidas durante las partidas. Se espera un comportamiento respetuoso entre los jugadores.</p>
                      <p><strong>2. Disponibilidad:</strong> Top Ten Online se ofrece "tal cual". Hacemos todo lo posible por mantener los servidores activos y las salas sincronizadas, pero no garantizamos un servicio ininterrumpido ni nos responsabilizamos de partidas perdidas o desconexiones.</p>
                      <p><strong>3. Propiedad Intelectual:</strong> La mecánica base está inspirada en los juegos de mesa de tipo party game. El código, diseño visual y estructura de esta web son propiedad de sus creadores.</p>
                    </>
                  )}
                </div>
                <div className="p-4 border-t border-purple-700/50 text-center">
                  <button onClick={() => setLegalModal(null)} className="bg-purple-700 hover:bg-purple-600 text-white font-bold py-2 px-8 rounded-xl transition-colors">Entendido</button>
                </div>
              </div>
            </div>
          )}

          {/* Banner Cookies */}
          {showCookies && screen === "HOME" && (
            <div className="fixed bottom-0 left-0 right-0 bg-purple-950 border-t-2 border-purple-500 p-4 z-50 flex flex-col md:flex-row items-center justify-between gap-4 animate-in slide-in-from-bottom">
              <div className="text-sm text-purple-200 max-w-3xl">
                <strong>Uso de cookies:</strong> Utilizamos almacenamiento local para que no pierdas tu partida si recargas la página, y cookies de terceros para mostrar anuncios. Al seguir navegando, aceptas nuestra <button onClick={() => setLegalModal('privacy')} className="underline text-yellow-400 hover:text-white">Política de Privacidad</button>.
              </div>
              <button onClick={acceptCookies} className="bg-green-500 hover:bg-green-400 text-white font-bold py-2 px-6 rounded-xl transition-colors whitespace-nowrap">
                ¡Entendido!
              </button>
            </div>
          )}
        </div>
      )}

      {screen === "LOADING" && (
        <div className="flex flex-col items-center justify-center min-h-screen bg-purple-900 text-white">
          <Loader2 className="animate-spin w-12 h-12 text-yellow-400 mb-4"/>
          <p className="font-bold animate-pulse">Conectando...</p>
          {renderAppLeaveButton()}
        </div>
      )}

      {screen === "GAME" && gameState && (
        <>
          {gameState.phase === "GAME_OVER" && (() => {
            const stats = gameState.stats || {};
            let flashId = null; let minTime = Infinity;
            let confusedId = null; let maxMisplaced = -1;
            
            const captainsArr = Object.entries(stats)
              .filter(([pid, s]) => s.ordered_cards > 0)
              .map(([pid, s]) => ({
                pid,
                name: gameState.players[pid]?.name || "Desconocido",
                correct: s.correctly_ordered || 0,
                total: s.ordered_cards || 0,
                ratio: (s.correctly_ordered || 0) / (s.ordered_cards || 1)
              }))
              .sort((a, b) => b.ratio - a.ratio || b.correct - a.correct);
              
            const top3 = captainsArr.slice(0, 3);
            const medals = ['🥇', '🥈', '🥉'];

            Object.entries(stats).forEach(([pid, s]) => {
              if (s.answers > 0 && (s.total_time / s.answers) < minTime) { 
                minTime = (s.total_time / s.answers); 
                flashId = pid; 
              }
              if (s.misplaced > maxMisplaced) { 
                maxMisplaced = s.misplaced; 
                confusedId = pid; 
              }
            });

            return (
              <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-purple-900 text-white text-center relative py-12">
                <Confetti width={width} height={height} recycle={false} numberOfPieces={500}/>
                {renderAppLeaveButton()}
                
                <div className="mb-4 mt-8">
                  <PartyPopper size={50} className="text-yellow-400 mx-auto"/>
                </div>
                <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 mb-6">¡FIN DEL JUEGO!</h1>
                
                {top3.length > 0 && (
                  <div className="w-full max-w-sm bg-purple-950/50 p-5 rounded-2xl border-2 border-yellow-500/50 mb-6 relative z-20">
                    <p className="text-yellow-400 font-black uppercase text-sm mb-3 flex justify-center items-center gap-2">
                      <Trophy size={18}/> Top Capitanes <Trophy size={18}/>
                    </p>
                    <div className="space-y-2 text-left">
                      {top3.map((cap, i) => (
                        <div key={cap.pid} className="flex justify-between items-center bg-purple-900/80 p-2 rounded-lg">
                          <span className="font-bold">{medals[i]} {cap.name}</span>
                          <span className="text-xs font-bold text-purple-300">{cap.correct}/{cap.total} aciertos</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="w-full max-w-sm space-y-3 mb-8 text-left relative z-20">
                  {flashId && gameState.players[flashId] && (
                    <div className="bg-blue-900/50 p-3 rounded-xl flex items-center gap-4 border border-blue-500/30">
                      <div className="bg-blue-500 p-2 rounded-full"><Zap className="text-white"/></div>
                      <div>
                        <p className="text-xs text-blue-300 font-bold uppercase">El más rápido</p>
                        <p className="font-bold">{gameState.players[flashId].name}</p>
                      </div>
                    </div>
                  )}
                  {confusedId && maxMisplaced > 0 && gameState.players[confusedId] && (
                    <div className="bg-red-900/50 p-3 rounded-xl flex items-center gap-4 border border-red-500/30">
                      <div className="bg-red-500 p-2 rounded-full"><Frown className="text-white"/></div>
                      <div>
                        <p className="text-xs text-red-300 font-bold uppercase">El Incomprendido</p>
                        <p className="font-bold">
                          {gameState.players[confusedId].name} <span className="text-xs font-normal">({maxMisplaced} fallos)</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <button onClick={() => send("RESTART_GAME")} className={`w-full max-w-sm bg-green-500 text-white p-5 rounded-2xl font-black text-xl shadow-lg border-b-4 border-green-700 flex items-center justify-center gap-3 ${btnClass} relative z-20`}>
                  <RotateCw size={24}/> JUGAR DE NUEVO
                </button>
              </div>
            );
          })()}

          {(gameState.phase === "LOBBY" || gameState.phase === "CAPTAIN_CHOOSING") && (
            <div className="flex flex-col items-center min-h-screen p-6 pt-16 pb-40 bg-purple-900 text-white relative overflow-y-auto">
              {renderFloatingEmojis()}
              {renderAppLeaveButton()}
              {renderRoomBadge()}
              
              <div className="mb-10 text-center mt-12">
                <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 mb-2">TOP TEN</h1>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${gameState.mode === 'fiesta' ? 'bg-pink-600' : gameState.mode === 'personalizado' ? 'bg-orange-600' : 'bg-blue-600'}`}>MODO: {gameState.mode}</span>
              </div>

              {renderPlayerList()}

              <div className="mb-6 relative z-20 flex justify-center w-full">
                <div className="bg-purple-950/50 p-2 rounded-2xl border border-purple-500/30 shadow-lg">
                  <iframe src="/ad-300x250.html" width="300" height="250" frameBorder="0" scrolling="no" allowTransparency="true" style={{ border: 'none', borderRadius: '8px' }} sandbox="allow-scripts allow-popups allow-same-origin"></iframe>
                </div>
              </div>

              {gameState.phase === "LOBBY" ? (
                <div className="text-center w-full max-w-xs relative z-20">
                  {gameState.captainId === CLIENT_ID ? (
                    <button onClick={() => { setLocalAnswers({}); send("START_ROUND"); }} className={`w-full bg-gradient-to-b from-green-400 to-green-600 text-white px-10 py-5 rounded-2xl font-black text-xl shadow-green-900/50 shadow-lg border-b-4 border-green-800 ${btnClass}`}>
                      ¡EMPEZAR RONDA!
                    </button>
                  ) : (
                    <p className="text-purple-300 animate-pulse bg-purple-800/50 p-4 rounded-xl font-bold">
                      Esperando al Capitán {gameState.players[gameState.captainId]?.name}...
                    </p>
                  )}
                </div>
              ) : (
                gameState.captainId === CLIENT_ID ? (
                  <div className="w-full max-w-xs animate-in zoom-in text-center relative z-20">
                    <UserCircle size={48} className="mx-auto text-orange-400 mb-2" />
                    <textarea className="w-full p-4 rounded-xl bg-white text-purple-900 font-bold outline-none mb-4 focus:ring-4 ring-orange-500" placeholder="Escribe el tema..." rows={3} value={customTheme} onChange={(e) => setCustomTheme(e.target.value)} />
                    <button onClick={() => send("START_ROUND", { customQuestion: customTheme })} disabled={!customTheme.trim()} className={`w-full bg-green-500 text-white p-5 rounded-2xl font-black text-xl border-b-4 border-green-700 ${btnClass}`}>
                      LANZAR TEMA
                    </button>
                  </div>
                ) : (
                  <div className="text-center animate-pulse relative z-20">
                    <Loader2 size={48} className="mx-auto text-orange-400 animate-spin mb-4" />
                    <p className="text-xl font-bold">El capitán está pensando...</p>
                  </div>
                )
              )}
              {renderReactionBar()}
            </div>
          )}

          {gameState.phase === "ANSWERING" && (() => {
            const isCaptain = gameState.captainId === CLIENT_ID;
            
            if (showRerollScreen && isCaptain && gameState.mode === "personalizado") {
              return (
                <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-purple-900 text-white relative">
                  {renderFloatingEmojis()}
                  {renderAppLeaveButton()}
                  <div className="w-full max-w-xs animate-in zoom-in text-center relative z-20">
                    <RefreshCw size={48} className="mx-auto text-orange-400 mb-2" />
                    <textarea className="w-full p-4 rounded-xl bg-white text-purple-900 font-bold outline-none mb-4 focus:ring-4 ring-orange-500" placeholder="Escribe tu nuevo tema..." rows={3} value={rerollTheme} onChange={(e) => setRerollTheme(e.target.value)} />
                    <div className="flex gap-2">
                      <button onClick={() => setShowRerollScreen(false)} className="flex-1 bg-red-500 p-4 rounded-xl font-bold border-b-4 border-red-700">CANCELAR</button>
                      <button onClick={() => { send("REROLL", { customQuestion: rerollTheme }); setCustomTheme(rerollTheme); setShowRerollScreen(false); }} disabled={!rerollTheme.trim()} className="flex-1 bg-green-500 p-4 rounded-xl font-bold border-b-4 border-green-700">CONFIRMAR</button>
                    </div>
                  </div>
                </div>
              );
            }

            const myHand = gameState.players[CLIENT_ID]?.hand || [];
            const myCompleted = gameState.players[CLIENT_ID]?.completedCount || 0;
            const iHaveWork = myHand.length > 0 && myCompleted < myHand.length;
            
            return (
              <div className="min-h-screen bg-purple-900 text-white p-6 flex flex-col relative pb-32">
                {renderFloatingEmojis()}
                {renderAppLeaveButton()}
                {renderRoomBadge()}
                
                <div className="bg-purple-800 p-4 rounded-2xl shadow-lg mb-6 border border-purple-700 relative mt-16 z-20">
                  <p className="text-xs uppercase text-yellow-400 font-bold mb-1">El Tema es:</p>
                  <h2 className="text-xl font-bold leading-tight">{gameState.question}</h2>
                  {isCaptain && (
                    <div className="mt-4 pt-4 border-t border-purple-700 flex justify-end">
                      {gameState.rerolls > 0 ? (
                        <button onClick={() => { 
                          if (gameState.mode === "personalizado") { 
                            setRerollTheme(""); 
                            setShowRerollScreen(true); 
                          } else { send("REROLL"); } 
                        }} className="flex items-center gap-2 text-xs font-bold bg-purple-900 px-3 py-2 rounded-lg hover:bg-purple-950 transition-colors">
                          <RefreshCw size={14} /> Cambiar Pregunta ({gameState.rerolls})
                        </button>
                      ) : (
                        <button onClick={() => { 
                          setAdCountdown(3); 
                          setShowRewardAd(true); 
                        }} className="flex items-center gap-2 text-xs font-bold bg-yellow-600 text-white px-3 py-2 rounded-lg hover:bg-yellow-500 transition-colors shadow-[0_0_10px_rgba(202,138,4,0.5)] animate-pulse">
                          <PlayCircle size={14} /> +3 Cambios (Ver Anuncio)
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {!iHaveWork ? (
                  <div className="flex-1 flex flex-col items-center">
                    <div className="mb-6 flex flex-col items-center animate-in fade-in relative z-20">
                      <p className="text-xl font-bold">¡Esperando al resto!</p>
                    </div>
                    <div className="w-full max-w-sm bg-purple-950/50 rounded-xl p-4 space-y-3 relative z-20">
                      <p className="text-sm font-bold text-purple-400 uppercase mb-2">Progreso...</p>
                      {Object.entries(gameState.players).map(([pid, p]) => {
                        if (p.totalCards === 0) return null;
                        const done = p.completedCount === p.totalCards;
                        return (
                          <div key={pid} className={`flex items-center justify-between bg-purple-800/80 p-3 rounded-lg border border-purple-700 ${!p.isOnline ? 'opacity-50' : ''}`}>
                            <span className="font-medium flex items-center gap-2">
                              {p.isCaptain && <Crown size={12} className="text-yellow-400"/>} 
                              {p.name} 
                              {!p.isOnline && <WifiOff size={14}/>}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-purple-300">{p.completedCount}/{p.totalCards}</span>
                              {done ? <CheckCircle2 size={18} className="text-green-400"/> : <Loader2 size={18} className="text-yellow-400 animate-spin"/>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {renderReactionBar()}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center animate-in zoom-in duration-300 relative z-20">
                    {myHand.map((intensity) => {
                      const isSent = gameState.players[CLIENT_ID].responses && gameState.players[CLIENT_ID].responses[intensity];
                      return (
                        <div key={intensity} className={`w-full mb-6 p-4 rounded-2xl border-2 transition-all ${isSent ? 'bg-green-900/50 border-green-600 opacity-50' : 'bg-purple-800 border-yellow-400 shadow-xl'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-bold uppercase text-purple-300">Intensidad</span>
                            <div className="w-8 h-8 rounded-full bg-white text-purple-900 font-black flex items-center justify-center">{intensity}</div>
                          </div>
                          {isSent ? (
                            <p className="text-lg font-bold text-green-200 italic">"{gameState.players[CLIENT_ID].responses[intensity]}"</p>
                          ) : (
                            <div className="flex gap-2">
                              <input className="flex-1 p-3 rounded-lg bg-purple-900 text-white border border-purple-600 outline-none focus:border-yellow-400" placeholder="Tu respuesta..." value={localAnswers[intensity] || ""} onChange={(e) => setLocalAnswers({...localAnswers, [intensity]: e.target.value})} />
                              <button onClick={() => submitCardAnswer(intensity, localAnswers[intensity])} className="bg-yellow-400 text-purple-900 p-3 rounded-lg font-bold active:scale-95">
                                <Send size={20}/>
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {(gameState.phase === "ORDERING" || gameState.phase === "REVEAL") && (() => {
            const isReveal = gameState.phase === "REVEAL";
            const sortedResponses = [...gameState.orderedResponses].sort((a, b) => a.intensity - b.intensity);
            const isGlobalPerfect = gameState.orderedResponses.every((item, i) => item.intensity === sortedResponses[i].intensity);
            const isCaptain = gameState.captainId === CLIENT_ID;
            
            return (
              <div className={`min-h-screen p-4 relative transition-colors duration-500 ${isReveal && isGlobalPerfect ? 'bg-green-900' : 'bg-purple-900'} text-white`}>
                {isReveal && isGlobalPerfect && <Confetti width={width} height={height} recycle={false} />}
                {renderAppLeaveButton()}
                {renderRoomBadge()}
                {renderFloatingEmojis()}
                
                <div className="max-w-md mx-auto pb-32 mt-16 relative z-20">
                  <div className="bg-purple-800/80 p-3 rounded-xl mb-4 text-center border border-purple-500/50">
                    <p className="font-bold text-sm">{gameState.question}</p>
                  </div>

                  {isReveal && isGlobalPerfect && (
                    <div className="mb-4 bg-yellow-400 text-purple-900 p-3 rounded-xl font-black text-center text-xl shadow-lg animate-bounce flex justify-center items-center gap-2">
                      <Trophy /> ¡ORDEN PERFECTO! <Trophy />
                    </div>
                  )}

                  <DragDropContext onDragEnd={isCaptain && !isReveal ? onDragEnd : () => {}}>
                    <Droppable droppableId="responses">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                          {gameState.orderedResponses.map((item, index) => {
                            const pName = gameState.players[item.pid]?.name || "???";
                            let cardColor = "bg-purple-800 border-purple-600";
                            
                            if (isReveal) {
                              if (item.intensity === sortedResponses[index].intensity) {
                                cardColor = "bg-green-700 border-green-500";
                              } else {
                                cardColor = "bg-red-900 border-red-500 opacity-90";
                              }
                            }

                            return (
                              <Draggable key={item.id} draggableId={String(item.id)} index={index} isDragDisabled={!isCaptain || isReveal}>
                                {(provided, snapshot) => (
                                  <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className={`p-3 rounded-xl shadow-lg flex items-center gap-3 transition-colors border ${snapshot.isDragging ? 'bg-purple-600 scale-105 border-yellow-400 z-50' : cardColor}`}>
                                    <div className="font-black text-2xl w-6 text-center opacity-50">{index + 1}</div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-bold text-lg leading-tight break-words">"{item.text}"</p>
                                      {isReveal && <p className="text-xs text-purple-200 font-bold uppercase mt-1 truncate">{pName}</p>}
                                    </div>
                                    {isReveal && (
                                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-slate-900 border-2 border-white text-white text-xl font-black">
                                        {item.intensity}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
                
                <div className={`fixed bottom-0 left-0 right-0 p-4 backdrop-blur border-t transition-colors z-50 ${isReveal && isGlobalPerfect ? 'bg-green-900/90 border-green-700' : 'bg-purple-900/90 border-purple-700'}`}>
                  <div className="max-w-md mx-auto">
                    {isCaptain && !isReveal && (
                      <button onClick={() => send("REVEAL")} className={`w-full bg-green-500 text-white font-black py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 text-xl border-b-4 border-green-700 ${btnClass}`}>
                        <Eye size={28}/> REVELAR
                      </button>
                    )}
                    {isCaptain && isReveal && (
                      <button onClick={() => send("NEXT_CAPTAIN")} className={`w-full bg-blue-500 text-white font-black py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 text-xl border-b-4 border-blue-700 ${btnClass}`}>
                        SIGUIENTE RONDA <ArrowRight size={28}/>
                      </button>
                    )}
                    {!isCaptain && (
                      <p className="text-center text-xs text-purple-300 animate-pulse">
                        Esperando al Capitán...
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </>
  );
}

export default App;